/**
 * System Status Component
 *
 * Displays comprehensive system health and data quality metrics.
 * Shows games, predictions, player props, and identifies issues.
 */

import { useState, useEffect, useCallback } from 'react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import LoadingSpinner from '../common/LoadingSpinner';
import { getSystemStatus, getStatusHistory } from '../../services/adminService';

// Sport display names and colors
const SPORT_CONFIG = {
  nba: { name: 'NBA', color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  nhl: { name: 'NHL', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  nfl: { name: 'NFL', color: 'text-green-400', bgColor: 'bg-green-500/20' },
  mlb: { name: 'MLB', color: 'text-red-400', bgColor: 'bg-red-500/20' },
  ncaa_f: { name: 'NCAAF', color: 'text-purple-400', bgColor: 'bg-purple-500/20' }
};

export default function SystemStatus() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusData, historyData] = await Promise.all([
        getSystemStatus(selectedDate),
        getStatusHistory(7)
      ]);
      setStatus(statusData);
      setHistory(historyData);
    } catch (err) {
      console.error('Error fetching status:', err);
      setError(err.message || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Loading system status..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <div className="text-red-400 text-lg mb-2">Error Loading Status</div>
          <div className="text-text-secondary">{error}</div>
          <button
            onClick={fetchStatus}
            className="mt-4 px-4 py-2 bg-accent-blue/20 text-accent-blue rounded-lg hover:bg-accent-blue/30 transition-colors"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  const { sports, player_props, data_quality, summary } = status || {};

  return (
    <div className="space-y-6">
      {/* Date Selector & Summary */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-text-primary">System Status</h2>
            <p className="text-sm text-text-secondary mt-1">
              Data quality and pipeline health monitoring
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="input-glow text-sm"
            />
            <button
              onClick={fetchStatus}
              className="px-3 py-2 bg-accent-blue/20 text-accent-blue rounded-lg hover:bg-accent-blue/30 transition-colors text-sm"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-bg-tertiary rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-text-primary">{summary?.total_games || 0}</div>
            <div className="text-sm text-text-secondary">Total Games</div>
          </div>
          <div className="bg-bg-tertiary rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-accent-blue">{summary?.total_predictions || 0}</div>
            <div className="text-sm text-text-secondary">Predictions</div>
          </div>
          <div className="bg-bg-tertiary rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-success">{summary?.total_props || 0}</div>
            <div className="text-sm text-text-secondary">Player Props</div>
          </div>
          <div className={`bg-bg-tertiary rounded-lg p-4 text-center ${summary?.games_missing_boxscores > 0 ? 'ring-2 ring-red-500/50' : ''}`}>
            <div className={`text-3xl font-bold ${summary?.games_missing_boxscores > 0 ? 'text-red-400' : 'text-success'}`}>
              {summary?.games_missing_boxscores || 0}
            </div>
            <div className="text-sm text-text-secondary">Missing Box Scores</div>
          </div>
        </div>
      </Card>

      {/* Data Quality Issues */}
      {(data_quality?.issues?.length > 0 || data_quality?.warnings?.length > 0) && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <span className="text-red-400">!</span> Data Quality Alerts
          </h3>

          {/* Critical Issues */}
          {data_quality?.issues?.length > 0 && (
            <div className="space-y-2 mb-4">
              <div className="text-sm font-medium text-red-400 uppercase tracking-wider">Issues</div>
              {data_quality.issues.map((issue, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <Badge variant="error" size="sm">!</Badge>
                  <div>
                    <div className="text-sm text-text-primary font-medium">
                      {issue.sport && <span className="mr-2">[{issue.sport}]</span>}
                      {issue.type.replace(/_/g, ' ').toUpperCase()}
                    </div>
                    <div className="text-sm text-text-secondary">{issue.message}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Warnings */}
          {data_quality?.warnings?.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-warning uppercase tracking-wider">Warnings</div>
              {data_quality.warnings.map((warning, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-warning/10 border border-warning/30 rounded-lg">
                  <Badge variant="warning" size="sm">!</Badge>
                  <div>
                    <div className="text-sm text-text-primary font-medium">
                      {warning.sport && <span className="mr-2">[{warning.sport}]</span>}
                      {warning.type.replace(/_/g, ' ').toUpperCase()}
                    </div>
                    <div className="text-sm text-text-secondary">{warning.message}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* No Issues Banner */}
      {data_quality?.issues?.length === 0 && data_quality?.warnings?.length === 0 && (
        <Card className="p-4 bg-success/10 border border-success/30">
          <div className="flex items-center gap-3">
            <div className="text-2xl">✓</div>
            <div>
              <div className="text-success font-medium">All Systems Healthy</div>
              <div className="text-sm text-text-secondary">No data quality issues detected</div>
            </div>
          </div>
        </Card>
      )}

      {/* Sports Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Games by Sport */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Games by Sport</h3>
          <div className="space-y-3">
            {Object.entries(sports || {}).map(([sport, data]) => {
              const config = SPORT_CONFIG[sport] || { name: sport.toUpperCase(), color: 'text-gray-400', bgColor: 'bg-gray-500/20' };
              const games = data.games || {};
              const hasIssue = games.missing_boxscores > 0;

              return (
                <div key={sport} className={`p-4 rounded-lg ${config.bgColor} ${hasIssue ? 'ring-2 ring-red-500/50' : ''}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`font-semibold ${config.color}`}>{config.name}</span>
                    <span className="text-text-primary font-mono">{games.total || 0} games</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div className="text-center">
                      <div className="text-text-primary font-mono">{games.scheduled || 0}</div>
                      <div className="text-text-secondary">Scheduled</div>
                    </div>
                    <div className="text-center">
                      <div className="text-text-primary font-mono">{games.in_progress || 0}</div>
                      <div className="text-text-secondary">Live</div>
                    </div>
                    <div className="text-center">
                      <div className="text-success font-mono">{games.completed || 0}</div>
                      <div className="text-text-secondary">Final</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-mono ${games.missing_boxscores > 0 ? 'text-red-400' : 'text-success'}`}>
                        {games.missing_boxscores || 0}
                      </div>
                      <div className="text-text-secondary">No Stats</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Predictions by Sport */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Predictions & Streaks</h3>
          <div className="space-y-3">
            {Object.entries(sports || {}).map(([sport, data]) => {
              const config = SPORT_CONFIG[sport] || { name: sport.toUpperCase(), color: 'text-gray-400', bgColor: 'bg-gray-500/20' };
              const preds = data.predictions || {};

              return (
                <div key={sport} className={`p-4 rounded-lg ${config.bgColor}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`font-semibold ${config.color}`}>{config.name}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <div className="text-accent-blue font-mono text-lg">{preds.game_predictions || 0}</div>
                      <div className="text-text-secondary">Game Preds</div>
                    </div>
                    <div className="text-center">
                      <div className="text-purple-400 font-mono text-lg">{preds.player_predictions || 0}</div>
                      <div className="text-text-secondary">Player Preds</div>
                    </div>
                    <div className="text-center">
                      <div className="text-success font-mono text-lg">{preds.active_streaks || 0}</div>
                      <div className="text-text-secondary">Active Streaks</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Player Props OLAP Status */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Player Props OLAP Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-bg-tertiary rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-text-primary">{player_props?.total || 0}</div>
            <div className="text-xs text-text-secondary">Total in OLAP</div>
          </div>
          <div className="bg-bg-tertiary rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-accent-blue">{player_props?.for_date?.total || 0}</div>
            <div className="text-xs text-text-secondary">For This Date</div>
          </div>
          <div className="bg-bg-tertiary rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-success">{player_props?.for_date?.with_ml_prediction || 0}</div>
            <div className="text-xs text-text-secondary">With ML Pred</div>
          </div>
          <div className="bg-bg-tertiary rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-warning">{player_props?.for_date?.with_sportsbook_line || 0}</div>
            <div className="text-xs text-text-secondary">With SB Line</div>
          </div>
        </div>

        {/* Props by Sport */}
        {player_props?.by_sport && Object.keys(player_props.by_sport).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(player_props.by_sport).map(([sport, count]) => {
              const config = SPORT_CONFIG[sport] || { name: sport.toUpperCase(), color: 'text-gray-400' };
              return (
                <div key={sport} className="px-3 py-1 bg-bg-tertiary rounded-full text-sm">
                  <span className={config.color}>{config.name}</span>
                  <span className="text-text-primary ml-2 font-mono">{count}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* 7-Day History */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">7-Day History</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary border-b border-border">
                <th className="text-left py-2 px-3">Date</th>
                <th className="text-center py-2 px-3">NBA</th>
                <th className="text-center py-2 px-3">NHL</th>
                <th className="text-center py-2 px-3">NFL</th>
                <th className="text-center py-2 px-3">Props</th>
              </tr>
            </thead>
            <tbody>
              {history.map((day) => (
                <tr key={day.date} className="border-b border-border/50 hover:bg-bg-tertiary/50">
                  <td className="py-2 px-3 font-mono text-text-primary">{day.date}</td>
                  <td className="py-2 px-3 text-center">
                    <span className="text-text-primary">{day.games?.nba?.total || 0}</span>
                    <span className="text-text-secondary mx-1">/</span>
                    <span className="text-accent-blue">{day.predictions?.nba || 0}</span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className="text-text-primary">{day.games?.nhl?.total || 0}</span>
                    <span className="text-text-secondary mx-1">/</span>
                    <span className="text-accent-blue">{day.predictions?.nhl || 0}</span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className="text-text-primary">{day.games?.nfl?.total || 0}</span>
                    <span className="text-text-secondary mx-1">/</span>
                    <span className="text-accent-blue">{day.predictions?.nfl || 0}</span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={day.props > 0 ? 'text-success' : 'text-red-400'}>{day.props}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-xs text-text-secondary mt-2">
            Format: Games / Predictions
          </div>
        </div>
      </Card>
    </div>
  );
}
